import React, { useState } from 'react';
import { Mail, Phone, Globe, Send, CheckCircle2, ArrowRight } from 'lucide-react';

export const Contact: React.FC = () => {
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    software: 'Tally Prime',
    message: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  const whatsappMessage = encodeURIComponent(
    `Hi Forty2 Team, I am contacting you from the forty2.online website.\nName: ${formData.name || 'Business Owner'}\nCompany: ${formData.company || 'Growing Business'}\nCurrent Accounting Tool: ${formData.software}\nI'd like to schedule an introductory consultation.`
  );

  return (
    <div style={{ paddingTop: '80px' }}>
      {/* Header */}
      <section
        style={{
          paddingTop: '64px',
          paddingBottom: '80px',
          borderBottom: '1px solid var(--border-subtle)',
          background: 'radial-gradient(ellipse 70% 50% at 50% 0%, var(--brand-red-glow-subtle), transparent)',
        }}
      >
        <div className="container-max" style={{ textAlign: 'center', maxWidth: '820px' }}>
          <span className="badge-pill badge-red" style={{ marginBottom: '16px' }}>
            CONNECT WITH FORTY2
          </span>
          <h1
            style={{
              fontSize: 'clamp(2.5rem, 5vw, 3.8rem)',
              fontWeight: 800,
              color: 'var(--text-primary)',
              letterSpacing: '-0.02em',
              marginBottom: '20px',
            }}
          >
            Every business deserves financial clarity.
          </h1>
          <p
            style={{
              fontSize: '1.2rem',
              color: 'var(--text-secondary)',
              lineHeight: '1.65',
              maxWidth: '720px',
              margin: '0 auto',
            }}
          >
            If your diagnostic score revealed gaps, that's where we start. Forty2 works with SMEs across India, the Middle East, and Southeast Asia to build the financial intelligence layer their business needs to grow with confidence.
          </p>
        </div>
      </section>

      {/* Main Content Form & Contact Info */}
      <section style={{ padding: '96px 0' }}>
        <div className="container-max">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
              gap: '48px',
              alignItems: 'flex-start',
            }}
          >
            {/* Left: Contact Channels & Regions */}
            <div>
              <span className="badge-pill badge-neutral font-mono" style={{ marginBottom: '16px' }}>
                DIRECT TOUCHPOINTS
              </span>
              <h2 style={{ fontSize: '1.85rem', fontWeight: 800, color: 'var(--text-primary)', marginBottom: '16px' }}>
                Speak with Our Founding Team & CA Partners
              </h2>
              <p style={{ fontSize: '1rem', color: 'var(--text-secondary)', lineHeight: '1.6', marginBottom: '32px' }}>
                Reach out directly for partnership inquiries, client onboarding, or a 1-on-1 walkthrough of the Forty2 platform.
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginBottom: '40px' }}>
                <a
                  href="mailto:ksr@forty2.online"
                  className="glass-panel"
                  style={{
                    padding: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    border: '1px solid var(--border-subtle)',
                  }}
                >
                  <div
                    style={{
                      width: '46px',
                      height: '46px',
                      borderRadius: '12px',
                      backgroundColor: 'rgba(229, 57, 53, 0.12)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Mail size={22} color="var(--brand-red)" />
                  </div>
                  <div>
                    <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      DIRECT EMAIL
                    </span>
                    <div style={{ fontSize: '1.05rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                      ksr@forty2.online
                    </div>
                  </div>
                </a>

                <a
                  href={`https://wa.me/919789367892?text=${whatsappMessage}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="glass-panel"
                  style={{
                    padding: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    border: '1px solid rgba(0, 230, 118, 0.25)',
                  }}
                >
                  <div
                    style={{
                      width: '46px',
                      height: '46px',
                      borderRadius: '12px',
                      backgroundColor: 'rgba(0, 230, 118, 0.12)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Phone size={22} color="var(--signal-green)" />
                  </div>
                  <div>
                    <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      WHATSAPP & CALL
                    </span>
                    <div style={{ fontSize: '1.05rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                      +91-9789 FORTY 2 (+91 97893 67892)
                    </div>
                  </div>
                </a>
              </div>

              {/* Regions Badge Box */}
              <div
                style={{
                  padding: '24px',
                  background: 'var(--border-subtle)',
                  borderRadius: '16px',
                  border: '1px solid var(--border-subtle)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                  <Globe size={18} color="var(--brand-red)" />
                  <span style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                    Regional Operations
                  </span>
                </div>
                <p style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                  Serving growth businesses across <strong>India</strong> (Chennai, Bengaluru, Mumbai, Delhi NCR), the <strong>Middle East / GCC</strong> (Dubai, Abu Dhabi, Riyadh), and <strong>Southeast Asia</strong> (Singapore, Malaysia).
                </p>
              </div>
            </div>

            {/* Right: Modern Contact Form */}
            <div
              className="glass-panel-elevated"
              style={{
                padding: '40px',
                border: '1px solid rgba(229, 57, 53, 0.3)',
              }}
            >
              <h3 style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '8px' }}>
                Schedule a Consultation
              </h3>
              <p style={{ fontSize: '0.92rem', color: 'var(--text-secondary)', marginBottom: '24px' }}>
                Fill in your details below and our Chartered Accountant advisory team will respond within 24 hours.
              </p>

              {!submitted ? (
                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.82rem', color: 'var(--text-secondary)', marginBottom: '6px' }}>
                      Full Name *
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Rahul Sharma"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '12px 14px',
                        borderRadius: '8px',
                        background: 'var(--bg-input)',
                        border: '1px solid var(--border-medium)',
                        color: 'var(--text-primary)',
                        fontSize: '0.9rem',
                      }}
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.82rem', color: 'var(--text-secondary)', marginBottom: '6px' }}>
                        Work Email *
                      </label>
                      <input
                        type="email"
                        required
                        placeholder="you@company.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '12px 14px',
                          borderRadius: '8px',
                          background: 'var(--bg-input)',
                          border: '1px solid var(--border-medium)',
                          color: 'var(--text-primary)',
                          fontSize: '0.9rem',
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '0.82rem', color: 'var(--text-secondary)', marginBottom: '6px' }}>
                        Phone / WhatsApp
                      </label>
                      <input
                        type="tel"
                        placeholder="+91 98765 43210"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '12px 14px',
                          borderRadius: '8px',
                          background: 'var(--bg-input)',
                          border: '1px solid var(--border-medium)',
                          color: 'var(--text-primary)',
                          fontSize: '0.9rem',
                        }}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.82rem', color: 'var(--text-secondary)', marginBottom: '6px' }}>
                        Company Name
                      </label>
                      <input
                        type="text"
                        placeholder="Acme Tech Labs"
                        value={formData.company}
                        onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '12px 14px',
                          borderRadius: '8px',
                          background: 'var(--bg-input)',
                          border: '1px solid var(--border-medium)',
                          color: 'var(--text-primary)',
                          fontSize: '0.9rem',
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '0.82rem', color: 'var(--text-secondary)', marginBottom: '6px' }}>
                        Current Accounting Tool
                      </label>
                      <select
                        value={formData.software}
                        onChange={(e) => setFormData({ ...formData, software: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '12px 14px',
                          borderRadius: '8px',
                          background: 'var(--bg-input)',
                          border: '1px solid var(--border-medium)',
                          color: 'var(--text-primary)',
                          fontSize: '0.9rem',
                        }}
                      >
                        <option value="Tally Prime">Tally Prime</option>
                        <option value="Zoho Books">Zoho Books</option>
                        <option value="QuickBooks">QuickBooks</option>
                        <option value="Excel / Manual">Excel / Manual</option>
                        <option value="Other ERP">Other ERP</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.82rem', color: 'var(--text-secondary)', marginBottom: '6px' }}>
                      Primary Financial Question or Pain Point
                    </label>
                    <textarea
                      rows={4}
                      placeholder="e.g. We need automated bookkeeping and a 90-day cash flow forecast to monitor our runway."
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '12px 14px',
                        borderRadius: '8px',
                        background: 'var(--bg-input)',
                        border: '1px solid var(--border-medium)',
                        color: 'var(--text-primary)',
                        fontSize: '0.9rem',
                        resize: 'vertical',
                      }}
                    />
                  </div>

                  <button type="submit" className="btn-primary" style={{ width: '100%', padding: '14px', marginTop: '8px' }}>
                    <span>Send Consultation Request</span>
                    <Send size={16} />
                  </button>
                </form>
              ) : (
                <div
                  style={{
                    padding: '32px 24px',
                    textAlign: 'center',
                    background: 'rgba(0, 230, 118, 0.08)',
                    borderRadius: '12px',
                    border: '1px solid rgba(0, 230, 118, 0.3)',
                  }}
                >
                  <CheckCircle2 size={42} color="var(--signal-green)" style={{ margin: '0 auto 16px auto' }} />
                  <h4 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '8px' }}>
                    Request Received, {formData.name}!
                  </h4>
                  <p style={{ fontSize: '0.92rem', color: 'var(--text-secondary)', lineHeight: '1.6', marginBottom: '20px' }}>
                    Our Chartered Accountant partners at <strong>ksr@forty2.online</strong> will review your business requirements and contact you within 24 hours.
                  </p>
                  <a
                    href={`https://wa.me/919789367892?text=${whatsappMessage}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-secondary"
                    style={{ display: 'inline-flex', padding: '10px 20px', color: 'var(--signal-green)' }}
                  >
                    <span>Instant Follow-up on WhatsApp</span>
                    <ArrowRight size={14} />
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
